import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { RootState } from '../../store/store';
import { queryParams } from '../../utils/query-string';
import { IListResponse } from '../../configs/constants';
import { IParams, IChallenge } from './challenges';
import { convertModelToFormData } from '../../utils/utility';

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.REACT_APP_API_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithRetry = retry(baseQuery, { maxRetries: 2 });

export const challengeApi = createApi({
  reducerPath: 'challengeApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: ['Challenge'],
  endpoints: (builder) => ({
    getChallenges: builder.query<
      IListResponse<IChallenge>,
      { page: number; pageSize: number; status?: string }
    >({
      query: ({ page, pageSize, status }) => {
        const params: IParams = {
          take: pageSize,
          skip: (page - 1) * pageSize,
        };
        if (status) params.status = status;
        return {
          url: `challenges?${queryParams(params)}`,
          method: 'GET',
        };
      },
      providesTags: ['Challenge'],
    }),
    addChallenge: builder.mutation<{ data: IChallenge; type: string }, any>({
      query: (body) => {
        return {
          url: 'challenges',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: ['Challenge'],
    }),
    updateChallenge: builder.mutation<{ data: IChallenge; type: string }, any>({
      query: (body) => {
        const { id } = body;
        delete body.id;
        return {
          url: `challenges/${id}`,
          method: 'PATCH',
          body: convertModelToFormData(body),
        };
      },
      invalidatesTags: ['Challenge'],
    }),
    deleteChallenge: builder.mutation<{ data: string; type: string }, any>({
      query: (id) => {
        return {
          url: `challenges/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: ['Challenge'],
    }),
  }),
});

export const {
  endpoints: { getChallenges },
} = challengeApi;
export const {
  useGetChallengesQuery,
  useAddChallengeMutation,
  useUpdateChallengeMutation,
  useDeleteChallengeMutation,
} = challengeApi;
